import { useContext, useEffect } from 'react';
import { SebastianStatus } from '~/constants/common/SebastianStatus';
import { useSebastianStatus } from '~/context/common/SebastianContext';
import UserVehicleContext from '~/context/vdp/UserVehicleContext';
import { useGetVehicleDetails } from '~/context/vdp/VehicleContext';
import useAlertStatus from '~/hooks/vdp/selectors/useAlertStatus';
import usePaymentSummary from '~/hooks/vdp/selectors/useGetPaymentSummary';
import useIsInExclusivePreview from '~/hooks/vdp/selectors/useIsInExclusivePreview';
import usePrimaryCtaButtonState from '~/hooks/vdp/selectors/usePrimaryCtaButtonState';
import { useVehiclePrice } from '~/hooks/vdp/useVehiclePrice';
import { generateErrorLogger } from '~/utilities/loggers';
import deriveVehicleDescription from '~/utilities/vdp/deriveVehicleDescription';

export type VdpPageContext = {
  description?: string;
  displayedPrice?: number;
  estimatedPricePlusFees?: number;
  estimatedTaxesAndFees?: number;
  incentivizedPrice?: number;
  make?: string;
  mileage?: number;
  model?: string;
  offering?: string;
  shippingFee?: number;
  trim?: string;
  vehicleId: number;
  year?: number;
};

const logSetPageContextError = generateErrorLogger(
  'useSendVdpSebastianPageContext.setPageContext'
);

export default function useSendVdpSebastianPageContext() {
  const sebastianStatus = useSebastianStatus();
  const { make, mileage, model, offering, trim, vehicleId, year } =
    useGetVehicleDetails();
  const { vehicleAvailability } = useContext(UserVehicleContext);
  const { finalPrice: displayedPrice } = useVehiclePrice();
  const {
    debits: { deliveryCharge: shippingFee, taxTitleReg: estimatedTaxesAndFees },
    totals: { totalPrice: estimatedPricePlusFees }
  } = usePaymentSummary();
  const {
    isReservedByOtherAndSubscribed,
    isReservedByUserAndKicked,
    type: ctaType
  } = usePrimaryCtaButtonState();
  const {
    isSold,
    notAvailableInCA,
    outOfMarket,
    outOfMarketNewCar,
    purchasePending,
    stopSaleRecall
  } = useAlertStatus();
  const isInExclusivePreview = useIsInExclusivePreview();

  const isOutOfArea = notAvailableInCA || outOfMarket || outOfMarketNewCar;
  const { description } = vehicleAvailability.isLoaded
    ? deriveVehicleDescription({
        ctaType,
        isInExclusivePreview,
        isOutOfArea,
        isReservedByOtherAndSubscribed,
        isReservedByUserAndKicked,
        isSold,
        purchasePending,
        stopSaleRecall
      })
    : { description: undefined };

  useEffect(() => {
    if (sebastianStatus !== SebastianStatus.Ready || !vehicleId) return;

    try {
      window.CarvanaChatUi?.setPageContext?.(
        {
          surface: 'VDP',
          surface_description:
            'Customer is viewing a vehicle detail page for a specific vehicle'
        },
        {
          ...(description && { description }),
          ...(displayedPrice && { displayedPrice }),
          ...(estimatedPricePlusFees && { estimatedPricePlusFees }),
          ...(estimatedTaxesAndFees && { estimatedTaxesAndFees }),
          ...(make && { make }),
          ...(mileage != null && { mileage }),
          ...(model && { model }),
          ...(offering && { offering }),
          ...(shippingFee != null && { shippingFee }),
          ...(trim && { trim }),
          vehicleId,
          ...(year && { year })
        }
      );
    } catch (err) {
      logSetPageContextError(err);
    }
  }, [
    description,
    displayedPrice,
    estimatedPricePlusFees,
    estimatedTaxesAndFees,
    make,
    mileage,
    model,
    offering,
    sebastianStatus,
    shippingFee,
    trim,
    vehicleId,
    year
  ]);
}
